import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useLoading } from "../common/LoadingContext";
import { useToast } from "../common/ToastContext";

import {
  FaSortUp,
  FaSortDown,
  FaCheckCircle,
  FaRegTimesCircle,
  FaSpinner,
  FaClock,
  FaExclamationTriangle,
  FaExclamationCircle,
  FaUndo,
} from "react-icons/fa";
import ProfileModal from "./ProfileModal";
import CnamModal from "./CnamModal";
import twilioSenderService from "../../services/twilioSenderService";

const RegisteredProfileTable = ({
  senders,
  sortConfig,
  handleSort,
  searchTerm,
  isRegexMode,
  getProfileFromSid,
  getMessagingServiceFromSid,
  onClaimCnam,
  onRefreshSender,
}) => {
  const [campaignTooltip, setCampaignTooltip] = useState(null);
  const [a2p10DlcTooltip, setA2P10DlcTooltip] = useState(null);
  const [selectedSender, setSelectedSender] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedMessagingService, setSelectedMessagingService] =
    useState(null);
  const [showSenderModal, setShowSenderModal] = useState(false);
  const [showCnamModal, setShowCnamModal] = useState(false);
  const { setIsLoading } = useLoading();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const columns = [
    { key: "messaging_service", label: "Messaging Service", width: "12%" },
    { key: "phone_number", label: "Phone Number", width: "12%" },
    { key: "cnam", label: "CNAM", width: "12%" },
    { key: "campaign_status", label: "Campaign Status", width: "12%" },
    { key: "a2p_10dlc_status", label: "A2P 10DLC Status", width: "12%" },
    { key: "refresh_status", label: "", width: "5%" },
  ];

  const campaignStatusMapToIcon = {
    IN_PROGRESS: <FaSpinner />,
    VERIFIED: <FaCheckCircle color="green" />,
    FAILED: <FaRegTimesCircle color="red" />,
  };

  const a2p10DlcStatusMapToIcon = {
    APPROVED: <FaCheckCircle color="green" />,
    FAILED: <FaRegTimesCircle color="red" />,
    IN_REVIEW: <FaSpinner />,
    PENDING: <FaClock color="yellow" />,
    SUSPENDED: <FaExclamationTriangle color="yellow" />,
  };

  const filteredSenders = useMemo(() => {
    if (!searchTerm) return senders;
    const regex = isRegexMode ? new RegExp(searchTerm, "i") : null;
    return senders.filter((sender) => {
      const combinedFields = `${sender.messaging_service_name} ${sender.phone_number} ${sender.cnam} ${sender.campaign_status} ${sender.a2p_10dlc_status}`;
      if (isRegexMode) {
        return regex.test(combinedFields);
      }
      return combinedFields.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [senders, searchTerm, isRegexMode]);

  const handleMouseEnterCampaignStatus = (status, id) => {
    let tooltipText = "";
    switch (status) {
      case "IN_PROGRESS":
        tooltipText = "Campaign verification is in progress.";
        break;
      case "VERIFIED":
        tooltipText = "Campaign has been verified.";
        break;
      case "FAILED":
        tooltipText = "Campaign verification failed.";
        break;
      default:
        tooltipText = "Unknown campaign status.";
    }
    setCampaignTooltip({ text: tooltipText, id: id });
  };

  const handleMouseEnterA2P10DLCStatus = (status, id) => {
    let tooltipText = "";
    switch (status) {
      case "APPROVED":
        tooltipText = "The A2P 10DLC registration has been approved.";
        break;
      case "FAILED":
        tooltipText = "The A2P 10DLC registration has failed.";
        break;
      case "IN_REVIEW":
        tooltipText = "The A2P 10DLC registration is currently in review.";
        break;
      case "PENDING":
        tooltipText = "The A2P 10DLC registration is pending submission.";
        break;
      case "SUSPENDED":
        tooltipText =
          "The A2P 10DLC registration has been suspended. Please contact support.";
        break;
      default:
        tooltipText = "Unknown A2P 10DLC status.";
    }
    setA2P10DlcTooltip({ text: tooltipText, id: id });
  };

  const handleSenderSelect = (sender) => {
    setSelectedSender(sender);
    setSelectedProfile(getProfileFromSid(sender.customer_profile_sid));
    setSelectedMessagingService(
      getMessagingServiceFromSid(sender.message_service_sid),
    );
    setShowSenderModal(true);
    navigate(`#${sender.id}`);
  };

  return (
    <div className="overflow-x-auto overflow-y-auto max-h-full">
      <table className="w-full table-fixed">
        <thead className="bg-gray-700 sticky top-0">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-600 transition-colors duration-150"
                style={{ width: column.width }}
                onClick={() => handleSort(column.key)}
              >
                <div className="flex items-center justify-center">
                  {column.label}
                  {sortConfig.key === column.key && (
                    <span className="ml-2">
                      {sortConfig.direction === "ascending" ? (
                        <FaSortUp className="text-blue-400" />
                      ) : (
                        <FaSortDown className="text-blue-400" />
                      )}
                    </span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700">
          {filteredSenders.map((sender) => (
            <tr
              key={sender.id}
              className="hover:bg-gray-700 transition-colors duration-150 cursor-pointer text-center"
              onClick={() => handleSenderSelect(sender)}
            >
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 overflow-x-auto">
                {sender.messaging_service_name || "N/A"}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 overflow-x-auto">
                <div className="flex items-center justify-center">
                  {sender.phone_number || "N/A"}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 overflow-x-auto">
                <div className="flex items-center justify-center">
                  {/* Assume CNAM is empty until phone numbers are implemented */}
                  <FaExclamationCircle
                    color="red"
                    title="No cnam associated with this sender"
                    onClick={(e) => {
                      setShowCnamModal(true);
                      e.stopPropagation();
                    }}
                  />
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 overflow-x-auto">
                <div
                  className="flex items-center justify-center"
                  onMouseEnter={() =>
                    handleMouseEnterCampaignStatus(
                      sender.campaign_status,
                      sender.id,
                    )
                  }
                  onMouseLeave={() => setCampaignTooltip(null)}
                >
                  {campaignStatusMapToIcon[sender.campaign_status] ||
                    sender.campaign_status ||
                    "N/A"}
                </div>
                {campaignTooltip && campaignTooltip.id === sender.id && (
                  <div className="absolute bg-gray-800 text-white text-xs rounded px-2 mt-1">
                    {campaignTooltip.text}
                  </div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 overflow-x-auto">
                <div
                  className="flex items-center justify-center"
                  onMouseEnter={() =>
                    handleMouseEnterA2P10DLCStatus(
                      sender.a2p_10dlc_status,
                      sender.id,
                    )
                  }
                  onMouseLeave={() => setA2P10DlcTooltip(null)}
                >
                  {a2p10DlcStatusMapToIcon[sender.a2p_10dlc_status] ||
                    sender.a2p_10dlc_status ||
                    "N/A"}
                </div>
                {a2p10DlcTooltip && a2p10DlcTooltip.id === sender.id && (
                  <div className="absolute bg-gray-800 text-white text-xs rounded px-2 mt-1">
                    {a2p10DlcTooltip.text}
                  </div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 overflow-x-auto">
                <FaUndo
                  title="Refresh sender data"
                  onClick={(e) => {
                    // For now, just re-open the profile modal which will trigger a data refresh
                    onRefreshSender(sender);
                    e.stopPropagation();
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showSenderModal && selectedSender && (
        <ProfileModal
          sender={selectedSender}
          profile={selectedProfile}
          messagingService={selectedMessagingService}
          onClose={() => setShowSenderModal(false)}
        />
      )}
      {showCnamModal && (
        <CnamModal
          onClose={() => setShowCnamModal(false)}
          onSubmit={onClaimCnam}
        />
      )}
    </div>
  );
};

export default RegisteredProfileTable;
